import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

const Portfolio = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const projects = [
    {
      title: 'Rénovation Fenêtres PVC',
      location: 'Toulouse Centre',
      image: 'https://public.readdy.ai/ai/img_res/f1d3742d3b13977c2d4caeac2bbcd14b.jpg',
      description: 'Remplacement complet de 8 fenêtres PVC avec double vitrage',
      category: 'Rénovation'
    },
    {
      title: 'Réparation Baie Vitrée',
      location: 'Colomiers',
      image: 'https://readdy.ai/api/search-image?query=large%20sliding%20glass%20door%20bay%20window%20repair%20showing%20smooth%20operation%20modern%20aluminum%20frame%20floor%20to%20ceiling%20glazing%20installed%20in%20contemporary%20living%20room%20simple%20clean%20background%20demonstrating%20professional%20repair%20quality%20and%20attention%20to%20detail&width=800&height=600&seq=portfolio-002&orientation=landscape',
      description: 'Réparation mécanisme coulissant et remplacement vitrage',
      category: 'Réparation'
    },
    {
      title: 'Installation Volets Roulants',
      location: 'Blagnac',
      image: 'https://readdy.ai/api/search-image?query=electric%20roller%20shutter%20installation%20showing%20modern%20motorized%20window%20covering%20system%20clean%20white%20slats%20smooth%20operation%20mechanism%20installed%20on%20contemporary%20home%20exterior%20simple%20neutral%20background%20highlighting%20professional%20installation%20and%20quality%20hardware&width=800&height=600&seq=portfolio-003&orientation=landscape',
      description: 'Pose de 6 volets roulants électriques avec télécommande',
      category: 'Installation'
    },
    {
      title: 'Restauration Fenêtres Bois',
      location: 'Tournefeuille',
      image: 'https://readdy.ai/api/search-image?query=restored%20wooden%20window%20frame%20showing%20beautiful%20natural%20wood%20grain%20texture%20expert%20carpentry%20refinishing%20work%20traditional%20craftsmanship%20quality%20double%20glazed%20glass%20installed%20in%20charming%20home%20simple%20clean%20background%20emphasizing%20restoration%20quality%20and%20wood%20beauty&width=800&height=600&seq=portfolio-004&orientation=landscape',
      description: 'Ponçage, traitement et peinture de fenêtres anciennes',
      category: 'Restauration'
    },
    {
      title: 'Double Vitrage',
      location: 'Ramonville',
      image: 'https://readdy.ai/api/search-image?query=modern%20double%20glazed%20window%20installation%20showing%20energy%20efficient%20glass%20panels%20excellent%20thermal%20insulation%20contemporary%20frame%20design%20installed%20in%20bright%20home%20interior%20simple%20neutral%20background%20highlighting%20advanced%20glazing%20technology%20and%20professional%20installation&width=800&height=600&seq=portfolio-005&orientation=landscape',
      description: 'Remplacement simple vitrage par double vitrage isolant',
      category: 'Isolation'
    },
    {
      title: 'Dépannage Urgent',
      location: 'Toulouse',
      image: 'https://readdy.ai/api/search-image?query=emergency%20window%20repair%20service%20showing%20technician%20securing%20broken%20window%20professional%20emergency%20response%20quality%20temporary%20protection%20installed%20in%20residential%20property%20simple%20clean%20background%20demonstrating%20rapid%20intervention%20and%20security%20measures&width=800&height=600&seq=portfolio-006&orientation=landscape',
      description: 'Sécurisation suite à effraction et remplacement vitrage',
      category: 'Urgence'
    }
  ];

  return (
    <section id="portfolio" ref={ref} className="py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Nos <strong className="text-teal-600">Réalisations</strong>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez quelques-unes de nos interventions récentes en réparation de fenêtres à Toulouse et ses environs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group cursor-pointer"
            >
              <div className="relative h-72 md:h-80 w-full rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                <img
                  src={project.image}
                  alt={`${project.title} - ${project.location}`}
                  title={`FenetrePro - ${project.title}`}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-4 right-4 bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {project.category}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-xl md:text-2xl font-bold mb-2">{project.title}</h3>
                  <div className="flex items-center text-sm mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <p className="text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                    {project.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-10 md:mt-12"
        >
          <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6">
            Plus de 2500 clients satisfaits nous font confiance pour leurs réparations de fenêtres
          </p>
          <a
            href="tel:0554542545"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Demandez votre devis gratuit
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
